/**
 * Kampanya Söylemleri — /kampanya-soylemleri.
 * Reklamın kendisinden (ad, kampanya adı, metin, o gün yayındaki başlık) okunan söylemlere göre gün bazlı harcama.
 * Veri /api/campaign-themes (theme_daily, her gece 05:40 yenilenir). Markaya erişimi olan herkes görür;
 * söylem sözlüğünü düzenleme yalnız backend EDITORS (rapor yanıtındaki can_edit).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AlertTriangle, ChevronDown, ChevronRight, Download, ImageOff, Info, LayoutGrid, List, Play, Plus, RefreshCw, Settings2, Trash2, X } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiFetch } from '../../utils/apiFetch';

const API = 'https://api.glowytics.com/api/campaign-themes';

// Bütçe kapsamı: Marka ve Performans ekiplerinin bütçeleri ayrı (Yataş, 26 Eki 2026). Sınıf backend'de
// kampanya adındaki "Branding" / "Performans"tan, yoksa Google kampanya tipi / Meta objective'inden.
const BUTCELER = [{ key: '', label: 'Tümü' }, { key: 'performans', label: 'Performans' }, { key: 'branding', label: 'Branding' }];
const butceAd = (b) => (BUTCELER.find((x) => x.key === b) || BUTCELER[0]).label;

const T = {
  zemin: '#F6F8FF', kart: '#FFFFFF', cizgi: '#E5E7EB', cizgi2: '#F3F4F6',
  metin: '#111827', metin2: '#374151', metin3: '#6B7280', mavi: '#436AEB', maviAcik: '#EEF2FF',
  kirmizi: '#DC2626', yesil: '#059669', turuncu: '#D97706',
};
const SERI = ['#436AEB', '#0EA5E9', '#14B8A6', '#F59E0B', '#EC4899', '#8B5CF6', '#EF4444', '#84CC16'];
const DIGER = '#94A3B8';
const AO_RENK = '#E2E8F0';
const PLAT_AD = { google: 'Google', meta: 'Meta' };
// Kampanya türü (Google advertising_channel_type | META); filtre çoklu seçim, boş = hepsi
const TUR_AD = { SEARCH: 'Arama', PERFORMANCE_MAX: 'Performance Max', VIDEO: 'YouTube', DEMAND_GEN: 'Demand Gen', DISPLAY: 'GDN', SHOPPING: 'Shopping', META: 'Meta', OTHER: 'Diğer' };
const turAd = (t) => TUR_AD[t] || t;
// kanal logoları (public/): Google türleri Google Ads logosu + tür adı
const TUR_LOGO = { META: '/deck/mecra/meta.svg', VIDEO: '/deck/mecra/youtube.svg', TIKTOK: '/deck/mecra/tiktok.svg' };
const turLogo = (t) => TUR_LOGO[t] || '/google_ads-512.webp';
const TUR_ALT = { SEARCH: 'Google Ads', PERFORMANCE_MAX: 'Google Ads', DEMAND_GEN: 'Google Ads', DISPLAY: 'Google Ads', SHOPPING: 'Google Ads', VIDEO: 'Google Ads', OTHER: 'Google Ads' };
const TABULAR = { fontVariantNumeric: 'tabular-nums' };

// ---- biçimleme ----
const n0 = (v) => Math.round(Number(v) || 0).toLocaleString('tr-TR');
const tl = (v) => `₺${Math.round(Number(v) || 0).toLocaleString('tr-TR')}`;
const tlKisa = (v) => {
  const x = Number(v) || 0;
  if (Math.abs(x) >= 1e6) return `₺${(x / 1e6).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}M`;
  if (Math.abs(x) >= 1e3) return `₺${(x / 1e3).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}B`;
  return tl(x);
};
const pctF = (v, d = 1) => (v == null ? '-' : `%${((Number(v) || 0) * 100).toLocaleString('tr-TR', { maximumFractionDigits: d })}`);
const roasF = (v) => (v == null || !isFinite(v) ? '-' : `${Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}x`);
const AYLAR = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const gunF = (s) => { if (!s) return '-'; const [y, m, d] = s.slice(0, 10).split('-'); return `${+d} ${AYLAR[+m - 1]} ${y.slice(2)}`; };
const ayF = (s) => `${AYLAR[+s.slice(5, 7) - 1]} ${s.slice(2, 4)}`;
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const etiket = (t) => (t.period ? `${t.name} · ${t.period}` : t.name);

const PRESETS = [
  { key: '30', label: 'Son 30 gün' }, { key: 'bu', label: 'Bu ay' }, { key: 'gecen', label: 'Geçen ay' },
  { key: '3', label: 'Son 3 ay' }, { key: '12', label: 'Son 12 ay' }, { key: 'hepsi', label: 'Tüm dönem' }, { key: 'ozel', label: 'Özel' },
];
const ILK_GUN = '2025-01-01';
function presetRange(key) {
  const now = new Date();
  if (key === '30') { const s = new Date(now); s.setDate(s.getDate() - 29); return [ymd(s), ymd(now)]; }
  if (key === 'bu') return [ymd(new Date(now.getFullYear(), now.getMonth(), 1)), ymd(now)];
  if (key === 'gecen') return [ymd(new Date(now.getFullYear(), now.getMonth() - 1, 1)), ymd(new Date(now.getFullYear(), now.getMonth(), 0))];
  if (key === '3') return [ymd(new Date(now.getFullYear(), now.getMonth() - 2, 1)), ymd(now)];
  if (key === '12') return [ymd(new Date(now.getFullYear(), now.getMonth() - 11, 1)), ymd(now)];
  if (key === 'hepsi') return [ILK_GUN, ymd(now)];
  return null;
}

async function cagir(yol, { method = 'GET', body, brandId } = {}) {
  const res = await apiFetch(`${API}${yol}`, {
    method,
    headers: { 'Content-Type': 'application/json', 'x-brand-id': brandId },
    body: body ? JSON.stringify(body) : undefined,
  });
  const govde = await res.json().catch(() => ({}));
  if (!res.ok) { const e = new Error(govde.error || `HTTP ${res.status}`); e.status = res.status; throw e; }
  return govde;
}
const qs = (o) => new URLSearchParams(Object.fromEntries(Object.entries(o).filter(([, v]) => v != null && v !== ''))).toString();

// ---- küçük bileşenler ----
function Kart({ children, style }) {
  return <div style={{ background: T.kart, border: `1px solid ${T.cizgi}`, borderRadius: 12, ...style }}>{children}</div>;
}
function Cip({ aktif, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '6px 12px', borderRadius: 8, fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', cursor: 'pointer',
      border: `1px solid ${aktif ? T.mavi : T.cizgi}`, background: aktif ? T.maviAcik : T.kart, color: aktif ? T.mavi : T.metin2,
    }}>{children}</button>
  );
}
function Dugme({ ikon: Ikon, children, onClick, disabled, ana, tehlike, kucuk }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: kucuk ? '4px 8px' : '7px 12px', borderRadius: 8,
      fontSize: kucuk ? 12 : 13, fontWeight: 500, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.55 : 1, whiteSpace: 'nowrap',
      border: `1px solid ${ana ? T.mavi : T.cizgi}`, background: ana ? T.mavi : T.kart, color: ana ? '#fff' : tehlike ? T.kirmizi : T.metin2,
    }}>{Ikon && <Ikon size={kucuk ? 13 : 15} />}{children}</button>
  );
}
function Kpi({ baslik, deger, alt }) {
  return (
    <Kart style={{ padding: '14px 16px', minWidth: 0 }}>
      <div style={{ fontSize: 12, color: T.metin3, marginBottom: 6 }}>{baslik}</div>
      <div style={{ fontSize: 'clamp(16px, 4.4vw, 22px)', fontWeight: 600, color: T.metin, ...TABULAR, overflowWrap: 'anywhere' }}>{deger}</div>
      {alt && <div style={{ fontSize: 12, color: T.metin3, marginTop: 4 }}>{alt}</div>}
    </Kart>
  );
}
const sb = { padding: '9px 10px', fontSize: 12, fontWeight: 600, color: T.metin3, textAlign: 'right', whiteSpace: 'nowrap', borderBottom: `1px solid ${T.cizgi}`, background: '#FAFBFF', position: 'sticky', top: 0, cursor: 'pointer', userSelect: 'none' };
const sd = { padding: '9px 10px', fontSize: 13, color: T.metin2, textAlign: 'right', whiteSpace: 'nowrap', borderBottom: `1px solid ${T.cizgi2}`, ...TABULAR };

// ---- kanal kırılımı (satır açılınca): kampanya türü başına harcama ve sonuç ----
function KanalKirilimi({ satir, brandId, start, end, types, butce, genislik }) {
  const [erisim, setErisim] = useState(null);
  const [reklamlar, setReklamlar] = useState(false);
  useEffect(() => {
    if (!(satir.meta_cost > 0)) { setErisim(null); return undefined; }
    let iptal = false;
    setErisim({ yukleniyor: true });
    cagir(`/reach?${qs({ start, end, types: types.length ? types.join(',') : null, budget: butce || null, theme_id: satir.theme_id ?? 'ao', seg_start: satir.period ? satir.first_date : null, seg_end: satir.period ? satir.last_date : null })}`, { brandId })
      .then((j) => { if (!iptal) setErisim(j); })
      .catch(() => { if (!iptal) setErisim({ hata: true }); });
    return () => { iptal = true; };
  }, [satir.key, satir.theme_id, satir.period, satir.first_date, satir.last_date, satir.meta_cost, brandId, start, end, types, butce]);
  const satirlar = satir.by_type || [];
  const h = { ...sb, position: 'static', cursor: 'default', background: '#F5F7FD' };
  return (
    // geniş söylem tablosu yatay kaydırılsa da kırılım kartın görünen genişliğinde, solda sabit kalır
    <div style={{ background: '#FBFCFF', borderTop: `1px solid ${T.cizgi}`, borderBottom: `1px solid ${T.cizgi}`, padding: '12px 14px 14px clamp(12px, 3vw, 36px)', position: 'sticky', left: 0, width: genislik || undefined, boxSizing: 'border-box' }}>
      <div style={{ overflowX: 'auto', background: T.kart, border: `1px solid ${T.cizgi}`, borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={{ ...h, textAlign: 'left' }}>Kanal</th>
            {['Harcama', 'Pay', 'Gösterim', 'Tıklama', 'TO', 'Satış', 'Ciro', 'ROAS', 'İzlenme', 'Reklam'].map((x) => <th key={x} style={h}>{x}</th>)}
          </tr></thead>
          <tbody>
            {satirlar.map((x) => (
              <tr key={x.type}>
                <td style={{ ...sd, textAlign: 'left' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <img src={turLogo(x.type)} alt={TUR_ALT[x.type] || turAd(x.type)} style={{ width: 18, height: 18, objectFit: 'contain', flex: 'none' }} />
                    <span style={{ color: T.metin, fontWeight: 500 }}>{turAd(x.type)}</span>
                  </span>
                </td>
                <td style={{ ...sd, color: T.metin, fontWeight: 500 }}>{tl(x.cost)}</td>
                <td style={sd}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ display: 'inline-block', width: 60, height: 6, background: T.cizgi2, borderRadius: 3, overflow: 'hidden' }}>
                      <span style={{ display: 'block', width: `${Math.min(100, x.share * 100)}%`, height: '100%', background: T.mavi }} />
                    </span>{pctF(x.share)}
                  </span>
                </td>
                <td style={sd}>{n0(x.impressions)}</td>
                <td style={sd}>{n0(x.clicks)}</td>
                <td style={sd}>{pctF(x.ctr, 2)}</td>
                <td style={sd}>{x.purchases ? n0(x.purchases) : '-'}</td>
                <td style={sd}>{x.revenue ? tl(x.revenue) : '-'}</td>
                <td style={sd}>{roasF(x.roas)}</td>
                <td style={sd}>{x.video_views ? n0(x.video_views) : '-'}</td>
                <td style={sd}>{n0(x.ad_count)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginTop: 10, fontSize: 12, color: T.metin2 }}>
        {satir.meta_cost > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <img src={TUR_LOGO.META} alt="Meta" style={{ width: 14, height: 14 }} />
            <b style={{ color: T.metin, fontWeight: 600 }}>Meta tekil erişim:</b>
            {!erisim || erisim.yukleniyor ? <span style={{ color: T.metin3 }}>hesaplanıyor…</span>
              : erisim.hata ? <span style={{ color: T.metin3 }}>alınamadı</span>
                : <span style={TABULAR}>{n0(erisim.reach)} kişi</span>}
            <span title="Erişim günler ve reklamlar arasında toplanamaz; Meta bu söylemin reklamları için kişileri tekilleştirir. Google reklam düzeyinde tekil erişim vermez." style={{ color: T.metin3, cursor: 'help' }}><Info size={13} /></span>
          </span>
        )}
        <span style={{ marginLeft: 'auto' }}>
          <Dugme kucuk onClick={() => setReklamlar((x) => !x)}>{reklamlar ? 'Reklamları gizle' : 'Reklamları göster'}</Dugme>
        </span>
      </div>
      {reklamlar && <div style={{ marginTop: 10, border: `1px solid ${T.cizgi}`, borderRadius: 10, overflow: 'hidden' }}><Reklamlar satir={satir} brandId={brandId} start={start} end={end} types={types} butce={butce} /></div>}
    </div>
  );
}

// ---- reklam listesi (kanal kırılımının altında, istenirse) ----
function Reklamlar({ satir, brandId, start, end, types, butce }) {
  const [veri, setVeri] = useState(null);
  const [hata, setHata] = useState(null);
  const [kartAcik, setKartAcik] = useState(false); // kart görünümü şimdilik yalnız EDITORS (can_edit)
  const [gorunum, setGorunum] = useState('kart');
  useEffect(() => {
    let iptal = false;
    setVeri(null); setHata(null);
    const p = { start, end, types: types.length ? types.join(',') : null, budget: butce || null, theme_id: satir.theme_id ?? 'ao', seg_start: satir.period ? satir.first_date : null, seg_end: satir.period ? satir.last_date : null };
    cagir(`/ads?${qs({ ...p, limit: 200 })}`, { brandId })
      .then((j) => { if (!iptal) { setVeri(j.ads || []); setKartAcik(!!j.can_edit); } })
      .catch((e) => { if (!iptal) setHata(e.message); });
    return () => { iptal = true; };
  }, [satir.key, satir.theme_id, satir.period, satir.first_date, satir.last_date, brandId, start, end, types, butce]);
  if (hata) return <div style={{ padding: 12, color: T.kirmizi, fontSize: 13 }}>Reklamlar yüklenemedi: {hata}</div>;
  if (!veri) return <div style={{ padding: 12, color: T.metin3, fontSize: 13 }}>Reklamlar yükleniyor…</div>;
  if (!veri.length) return <div style={{ padding: 12, color: T.metin3, fontSize: 13 }}>Bu aralıkta harcaması olan reklam yok.</div>;
  const ao = satir.theme_id == null;
  const kart = kartAcik && gorunum === 'kart';
  return (
    <>
    {kartAcik && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', background: '#FBFCFF' }}>
        <Cip aktif={gorunum === 'kart'} onClick={() => setGorunum('kart')}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><LayoutGrid size={13} />Kart</span></Cip>
        <Cip aktif={gorunum === 'tablo'} onClick={() => setGorunum('tablo')}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><List size={13} />Tablo</span></Cip>
        <span style={{ fontSize: 11, color: T.metin3, marginLeft: 6 }}>Kart görünümü şimdilik yalnız size açık</span>
      </div>
    )}
    {kart ? <ReklamKartlari veri={veri} ao={ao} brandId={brandId} /> : (
    <div style={{ overflowX: 'auto', background: '#FBFCFF', borderTop: `1px solid ${T.cizgi}` }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>
          {['Tür', 'Reklam', ao ? 'Metin örneği' : 'Söylemin geçtiği yer', 'Dayanak', 'İlk / son gün', 'Harcama', 'Gösterim', 'Tıklama', 'Satış', 'Ciro', 'İzlenme'].map((h, i) => (
            <th key={h} style={{ ...sb, position: 'static', cursor: 'default', textAlign: i < 4 ? 'left' : 'right', background: '#F5F7FD' }}>{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {veri.map((a) => (
            <tr key={`${a.platform}|${a.ad_entity_key}`}>
              <td style={{ ...sd, textAlign: 'left' }}>{a.ctype ? turAd(a.ctype) : PLAT_AD[a.platform]}</td>
              <td style={{ ...sd, textAlign: 'left', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }} title={`${a.ad_name || ''}\n${a.campaign_name || ''}`}>
                <div style={{ color: T.metin, overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.ad_name}</div>
                {a.campaign_name && a.campaign_name !== a.ad_name && <div style={{ fontSize: 11, color: T.metin3, overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.campaign_name}</div>}
              </td>
              <td style={{ ...sd, textAlign: 'left', whiteSpace: 'normal', minWidth: 220, maxWidth: 380 }}>
                {ao ? <span style={{ color: T.metin3 }}>{a.text_sample || '-'}</span>
                  : (a.evidence && a.evidence.length ? a.evidence.map((e, i) => <div key={i} style={{ fontSize: 12, marginBottom: 2 }}>“{e}”</div>) : '-')}
              </td>
              <td style={{ ...sd, textAlign: 'left', fontSize: 12, color: T.metin3 }}>{a.method}</td>
              <td style={{ ...sd, fontSize: 12 }}>{gunF(a.first_date)} – {gunF(a.last_date)}</td>
              <td style={sd}>{tl(a.cost)}</td>
              <td style={sd}>{n0(a.impressions)}</td>
              <td style={sd}>{n0(a.clicks)}</td>
              <td style={sd}>{a.purchases ? n0(a.purchases) : '-'}</td>
              <td style={sd}>{a.revenue ? tl(a.revenue) : '-'}</td>
              <td style={sd}>{a.video_views ? n0(a.video_views) : '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {veri.length >= 200 && <div style={{ padding: 8, fontSize: 12, color: T.metin3 }}>En yüksek harcamalı 200 reklam gösteriliyor.</div>}
    </div>
    )}
    </>
  );
}

// ---- reklam kartları: önce görsel, altında rakamlar ve söylemin geçtiği metin ----
const KART_ADIM = 24;
const satirKes = (n) => ({ display: '-webkit-box', WebkitLineClamp: n, WebkitBoxOrient: 'vertical', overflow: 'hidden' });

function ReklamGorseli({ a, brandId }) {
  const m = a.media;
  const ilk = m ? (m.kind === 'meta' ? (m.creative_id ? `${API}/creative/meta/${m.creative_id}?brandId=${encodeURIComponent(brandId)}` : m.fallback) : m.src) : null;
  const [src, setSrc] = useState(ilk);
  const [bozuk, setBozuk] = useState(false);
  useEffect(() => { setSrc(ilk); setBozuk(false); }, [ilk]);
  const kutu = { position: 'relative', aspectRatio: '1 / 1', background: '#F3F4F6', borderBottom: `1px solid ${T.cizgi2}`, overflow: 'hidden' };
  if (src && !bozuk) {
    const resim = (
      <img src={src} alt={a.ad_name || ''} loading="lazy"
        // gizli / silinmiş YouTube videosunda ytimg 120x90 gri yer tutucu döner
        onLoad={(e) => { if (m.kind === 'video' && e.currentTarget.naturalWidth <= 120) setBozuk(true); }}
        onError={() => { if (m && m.kind === 'meta' && m.fallback && src !== m.fallback) setSrc(m.fallback); else setBozuk(true); }}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
    );
    if (m.kind === 'video' && m.yt) {
      return (
        <a href={`https://www.youtube.com/watch?v=${m.yt}`} target="_blank" rel="noreferrer" style={{ ...kutu, display: 'block' }} title="Videoyu YouTube'da aç">
          {resim}
          <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ width: 44, height: 44, borderRadius: 22, background: 'rgba(17,24,39,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Play size={20} color="#fff" fill="#fff" /></span>
          </span>
        </a>
      );
    }
    return <div style={kutu}>{resim}</div>;
  }
  // görsel yok: Arama reklamında metin önizlemesi, diğerlerinde boş kutu
  if (a.ctype === 'SEARCH' && a.headlines && a.headlines.length) {
    return (
      <div style={{ ...kutu, background: T.kart, padding: 14, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 6 }}>
        <span style={{ fontSize: 11, color: T.metin3, fontWeight: 600 }}>Sponsorlu</span>
        <span style={{ fontSize: 15, lineHeight: 1.3, color: '#1A0DAB', ...satirKes(3) }}>{a.headlines.join(' | ')}</span>
        {a.descriptions && a.descriptions[0] && <span style={{ fontSize: 12, lineHeight: 1.4, color: T.metin2, ...satirKes(4) }}>{a.descriptions[0]}</span>}
      </div>
    );
  }
  return (
    <div style={{ ...kutu, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, color: T.metin3 }}>
      {m && m.kind === 'video' ? <Play size={22} /> : <ImageOff size={22} />}
      <span style={{ fontSize: 11 }}>{m && m.kind === 'video' ? 'Video önizlemesi yok' : 'Görsel yok'}</span>
      {m && m.kind === 'video' && m.yt && <a href={`https://www.youtube.com/watch?v=${m.yt}`} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: T.mavi }}>YouTube'da aç</a>}
    </div>
  );
}

function ReklamKartlari({ veri, ao, brandId }) {
  const [adet, setAdet] = useState(KART_ADIM);
  const kucuk = { fontSize: 11, color: T.metin3 };
  const deger = { fontSize: 13, color: T.metin, fontWeight: 600, ...TABULAR };
  return (
    <div style={{ background: '#FBFCFF', borderTop: `1px solid ${T.cizgi}`, padding: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
        {veri.slice(0, adet).map((a) => {
          // kanıtın reklam / kampanya adı olanı kartta tekrar gibi duruyor; yalnız metin kanıtı, yoksa reklam metni
          const adlar = new Set([a.ad_name, a.campaign_name, a.group_name].filter(Boolean));
          const kanit = (a.evidence || []).filter((e) => !adlar.has(e));
          const yedek = a.primary_text || a.text_sample;
          // Google video reklamında "metin" çoğu zaman yalnız işletme adı; kısa olanı gösterme
          const metin = !ao && kanit.length ? kanit.map((e) => `“${e}”`).join(' ') : (yedek && yedek.length >= 25 ? yedek : null);
          const roas = a.revenue && a.cost ? a.revenue / a.cost : null;
          const ikinci = a.purchases ? [['Satış', n0(a.purchases)], ['ROAS', roasF(roas)]]
            : a.video_views ? [['İzlenme', n0(a.video_views)], ['Tıklama', n0(a.clicks)]]
              : [['Gösterim', n0(a.impressions)], ['Tıklama', n0(a.clicks)]];
          return (
            <div key={`${a.platform}|${a.ad_entity_key}`} style={{ background: T.kart, border: `1px solid ${T.cizgi}`, borderRadius: 10, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <ReklamGorseli a={a} brandId={brandId} />
              <div style={{ padding: '10px 12px 12px', display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <img src={turLogo(a.ctype || 'META')} alt="" style={{ width: 14, height: 14, objectFit: 'contain', flex: 'none' }} />
                  <span style={{ fontSize: 11, color: T.metin2, fontWeight: 500 }}>{a.ctype ? turAd(a.ctype) : PLAT_AD[a.platform]}</span>
                  <span style={{ ...kucuk, marginLeft: 'auto', whiteSpace: 'nowrap' }}>{gunF(a.first_date)} – {gunF(a.last_date)}</span>
                </div>
                <div title={`${a.ad_name || ''}\n${a.campaign_name || ''}`}>
                  <div style={{ fontSize: 13, color: T.metin, fontWeight: 500, lineHeight: 1.3, ...satirKes(2), wordBreak: 'break-word' }}>{a.ad_name}</div>
                  {a.campaign_name && a.campaign_name !== a.ad_name && <div style={{ ...kucuk, marginTop: 2, ...satirKes(1), wordBreak: 'break-all' }}>{a.campaign_name}</div>}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: 6, paddingTop: 8, borderTop: `1px solid ${T.cizgi2}` }}>
                  <div><div style={kucuk}>Harcama</div><div style={deger}>{tl(a.cost)}</div></div>
                  {ikinci.map(([b, v]) => <div key={b}><div style={kucuk}>{b}</div><div style={deger}>{v}</div></div>)}
                </div>
                {metin && <div style={{ fontSize: 12, color: T.metin2, lineHeight: 1.4, ...satirKes(3) }} title={metin}>{metin}</div>}
              </div>
            </div>
          );
        })}
      </div>
      {veri.length > adet && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
          <Dugme kucuk onClick={() => setAdet((x) => x + KART_ADIM)}>Daha fazla göster ({n0(veri.length - adet)} reklam daha)</Dugme>
        </div>
      )}
      {veri.length >= 200 && adet >= veri.length && <div style={{ paddingTop: 8, fontSize: 12, color: T.metin3, textAlign: 'center' }}>En yüksek harcamalı 200 reklam gösteriliyor.</div>}
    </div>
  );
}

// ---- söylem sözlüğü yönetimi (yalnız can_edit) ----
const desenMetni = (p) => (p || []).join(', ');
const desenListe = (s) => String(s || '').split(',').map((x) => x.trim()).filter(Boolean);

function YonetimPaneli({ brandId, start, end, onKapat, onDegisti }) {
  const [liste, setListe] = useState(null);
  const [adaylar, setAdaylar] = useState(null);
  const [hata, setHata] = useState(null);
  const [yeni, setYeni] = useState({ name: '', patterns: '', scope: 'all', weak: false });
  const [duzen, setDuzen] = useState(null);
  const [birlestir, setBirlestir] = useState({ from: '', into: '' });
  const [sekme, setSekme] = useState('sozluk');

  const yukle = useCallback(() => {
    cagir('/themes', { brandId }).then(setListe).catch((e) => setHata(e.message));
  }, [brandId]);
  useEffect(() => { yukle(); }, [yukle]);
  useEffect(() => {
    if (sekme !== 'aday' || adaylar) return;
    cagir(`/candidates?${qs({ start, end })}`, { brandId }).then((j) => setAdaylar(j.candidates || [])).catch((e) => setHata(e.message));
  }, [sekme, adaylar, brandId, start, end]);

  const islem = async (method, yol, body) => {
    setHata(null);
    try { await cagir(yol, { method, body, brandId }); yukle(); onDegisti(); return true; } catch (e) { setHata(e.message); return false; }
  };
  const ekle = async () => {
    if (await islem('POST', '/themes', { name: yeni.name, patterns: desenListe(yeni.patterns), scope: yeni.scope, weak: yeni.weak })) setYeni({ name: '', patterns: '', scope: 'all', weak: false });
  };
  const kaydet = async () => {
    if (await islem('PATCH', `/themes/${duzen.id}`, { name: duzen.name, patterns: desenListe(duzen.patterns), scope: duzen.scope, weak: duzen.weak })) setDuzen(null);
  };
  const giris = { padding: '6px 8px', fontSize: 13, border: `1px solid ${T.cizgi}`, borderRadius: 8, minWidth: 0 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.35)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }} onClick={onKapat}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(720px, 100%)', height: '100%', background: T.kart, overflowY: 'auto', padding: 20, boxShadow: '-8px 0 24px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ fontSize: 17, fontWeight: 600, color: T.metin, margin: 0 }}>Söylem sözlüğü</h2>
          <button onClick={onKapat} style={{ marginLeft: 'auto', border: 'none', background: 'none', cursor: 'pointer', color: T.metin3 }} aria-label="Kapat"><X size={20} /></button>
        </div>
        <p style={{ fontSize: 12, color: T.metin3, margin: '0 0 12px' }}>
          Değişiklikten birkaç saniye sonra tüm dönem yeniden hesaplanır (yaklaşık 1 dakika). Desenler büyük/küçük harf, Türkçe karakter ve boşluktan bağımsız aranır: “Efsane Uyku” deseni “EfsaneUykuGunleri” adını da yakalar.
        </p>
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          <Cip aktif={sekme === 'sozluk'} onClick={() => setSekme('sozluk')}>Söylemler</Cip>
          <Cip aktif={sekme === 'aday'} onClick={() => setSekme('aday')}>Yakalanmayan ifadeler</Cip>
        </div>
        {hata && <div style={{ padding: 10, background: '#FEF2F2', color: T.kirmizi, borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{hata}</div>}

        {sekme === 'sozluk' && (
          <>
            <Kart style={{ padding: 12, marginBottom: 14, background: '#FAFBFF' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.metin, marginBottom: 8 }}>Yeni söylem</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.4fr)', gap: 8 }}>
                <input placeholder="Söylem adı (ör. Efsane Uyku Günleri)" value={yeni.name} onChange={(e) => setYeni({ ...yeni, name: e.target.value })} style={giris} />
                <input placeholder="Ek desenler, virgülle (ör. EfsaneUyku, BF-Efsane)" value={yeni.patterns} onChange={(e) => setYeni({ ...yeni, patterns: e.target.value })} style={giris} />
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginTop: 8, fontSize: 12, color: T.metin2 }}>
                <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}><input type="checkbox" checked={yeni.scope === 'name'} onChange={(e) => setYeni({ ...yeni, scope: e.target.checked ? 'name' : 'all' })} />Yalnız reklam / kampanya adında ara</label>
                <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}><input type="checkbox" checked={yeni.weak} onChange={(e) => setYeni({ ...yeni, weak: e.target.checked })} />Sürekli hat (başka söylem varsa ona bırak)</label>
                <span style={{ marginLeft: 'auto' }}><Dugme ikon={Plus} ana onClick={ekle} disabled={!yeni.name.trim()}>Ekle</Dugme></span>
              </div>
            </Kart>

            {liste && liste.length > 1 && (
              <Kart style={{ padding: 12, marginBottom: 14, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: T.metin2 }}>Birleştir:</span>
                <select value={birlestir.from} onChange={(e) => setBirlestir({ ...birlestir, from: e.target.value })} style={{ ...giris, maxWidth: 220 }}>
                  <option value="">söylem…</option>{liste.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
                <span style={{ fontSize: 13, color: T.metin3 }}>→</span>
                <select value={birlestir.into} onChange={(e) => setBirlestir({ ...birlestir, into: e.target.value })} style={{ ...giris, maxWidth: 220 }}>
                  <option value="">içine…</option>{liste.filter((x) => String(x.id) !== birlestir.from).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
                <Dugme disabled={!birlestir.from || !birlestir.into} onClick={async () => { if (await islem('POST', '/themes/merge', { into_id: +birlestir.into, from_ids: [+birlestir.from] })) setBirlestir({ from: '', into: '' }); }}>Birleştir</Dugme>
              </Kart>
            )}

            {!liste ? <div style={{ fontSize: 13, color: T.metin3 }}>Yükleniyor…</div> : (
              <div>
                {liste.map((x) => (duzen && duzen.id === x.id ? (
                  <Kart key={x.id} style={{ padding: 10, marginBottom: 8, borderColor: T.mavi }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.4fr)', gap: 8 }}>
                      <input value={duzen.name} onChange={(e) => setDuzen({ ...duzen, name: e.target.value })} style={giris} />
                      <input value={duzen.patterns} onChange={(e) => setDuzen({ ...duzen, patterns: e.target.value })} style={giris} placeholder="desenler, virgülle" />
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginTop: 8, fontSize: 12 }}>
                      <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}><input type="checkbox" checked={duzen.scope === 'name'} onChange={(e) => setDuzen({ ...duzen, scope: e.target.checked ? 'name' : 'all' })} />Yalnız ad</label>
                      <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}><input type="checkbox" checked={duzen.weak} onChange={(e) => setDuzen({ ...duzen, weak: e.target.checked })} />Sürekli hat</label>
                      <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                        <Dugme kucuk onClick={() => setDuzen(null)}>Vazgeç</Dugme>
                        <Dugme kucuk ana onClick={kaydet}>Kaydet</Dugme>
                      </span>
                    </div>
                  </Kart>
                ) : (
                  <div key={x.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 4px', borderBottom: `1px solid ${T.cizgi2}` }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13, color: T.metin, fontWeight: 500 }}>
                        {x.name}
                        {x.scope === 'name' && <span style={{ marginLeft: 6, fontSize: 11, color: T.metin3 }}>yalnız ad</span>}
                        {x.weak && <span style={{ marginLeft: 6, fontSize: 11, color: T.turuncu }}>sürekli hat</span>}
                      </div>
                      {(x.patterns || []).length > 0 && <div style={{ fontSize: 11, color: T.metin3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{desenMetni(x.patterns)}</div>}
                    </div>
                    <div style={{ fontSize: 12, color: T.metin2, ...TABULAR, whiteSpace: 'nowrap' }}>{tlKisa(x.cost)}</div>
                    <Dugme kucuk onClick={() => setDuzen({ id: x.id, name: x.name, patterns: desenMetni(x.patterns), scope: x.scope, weak: x.weak })}>Düzenle</Dugme>
                    <Dugme kucuk tehlike ikon={Trash2} onClick={() => { if (window.confirm(`“${x.name}” silinsin mi? Harcaması always-on'a ya da diğer söylemlere geçer.`)) islem('DELETE', `/themes/${x.id}`); }} />
                  </div>
                )))}
              </div>
            )}
          </>
        )}

        {sekme === 'aday' && (
          <div>
            <p style={{ fontSize: 12, color: T.metin3, margin: '0 0 10px' }}>Şu an always-on'da kalan reklamlarda geçen, söylem gibi görünen ifadeler (seçili aralık, always-on harcamasına göre). İndirim oranı ya da sürekli başlık olanları eklemeyin.</p>
            {!adaylar ? <div style={{ fontSize: 13, color: T.metin3 }}>Yükleniyor…</div> : adaylar.map((c, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '7px 4px', borderBottom: `1px solid ${T.cizgi2}` }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13, color: T.metin }}>{c.text}</div>
                  <div style={{ fontSize: 11, color: T.metin3 }}>{c.source} · {n0(c.ads)} reklam</div>
                </div>
                <div style={{ fontSize: 12, ...TABULAR, whiteSpace: 'nowrap' }}>{tlKisa(c.cost)}</div>
                <Dugme kucuk onClick={() => { setYeni({ name: c.text.replace(/[!.]+$/, ''), patterns: '', scope: 'all', weak: false }); setSekme('sozluk'); }}>Söylem yap</Dugme>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Excel ----
function excelYaz({ rapor, markaAd, start, end, types, butce }) {
  const satirlar = [...rapor.themes, rapor.always_on];
  const ozet = satirlar.map((t) => ({
    'Söylem': t.name, 'Dönem': t.period || '', 'Yoğun dönem başı': t.peak_start || '', 'Yoğun dönem sonu': t.peak_end || '',
    'Harcama (₺)': Math.round(t.cost), 'Pay': t.share, 'Meta harcama (₺)': Math.round(t.meta_cost), 'Google harcama (₺)': Math.round(t.google_cost),
    'Gösterim': t.impressions, 'Tıklama': t.clicks, 'TO': t.ctr, 'TBM (₺)': t.cpc,
    'Satış (adet)': Math.round(t.purchases || 0), 'Ciro (₺)': Math.round(t.revenue || 0), 'ROAS': t.roas, 'İzlenme': t.video_views,
    'Reklam sayısı': t.ad_count,
  }));
  const aylik = satirlar.map((t) => {
    const o = { 'Söylem': etiket(t) };
    for (const m of rapor.months) o[ayF(m)] = Math.round((t.monthly || {})[m] || 0);
    o['Toplam'] = Math.round(t.cost);
    return o;
  });
  const notlar = [
    ['Kampanya Söylemleri', `${markaAd} · ${start} – ${end}`],
    ['Kampanya türü', types && types.length ? types.map(turAd).join(', ') : 'Hepsi'],
    ['Bütçe', butce ? `${butceAd(butce)} (kampanya adındaki Branding / Performans; yoksa Google kampanya tipi ya da Meta hedefi)` : 'Tümü'],
    ['Tanım', 'Söyleme yatırım = o söylemi taşıyan reklamlara o günlerde harcanan tutar. Söylem yalnız reklamın kendisinden okunur (ad, kampanya adı, metin, o gün yayındaki başlık).'],
    ['Always-on', 'Söylem taşımayan harcama. Toplam, platform harcamasıyla aynıdır.'],
    ['Google', 'Arama ve Demand Gen reklamlarında her gün yayındaki başlık seti kullanılır. PMax ve günlük başlık verisi olmayan reklamlarda metindeki söylem yalnız o söylemin aynı günlerde markanın başka reklamlarında yayında olduğu dönemde sayılır.'],
    ['Bölüşüm', 'Aynı gün aynı reklamda birden fazla söylem varsa harcama gösterime göre, gösterim verisi yoksa eşit bölünür.'],
    ['Dönem', 'Her yıl tekrar eden söylemler dönemlerine ayrılır (75 günden uzun ara yeni dönem). Yoğun dönem, harcamanın ortadaki %90\'ının gerçekleştiği aralıktır.'],
    ['Satış / ciro', 'Meta satın almaları + Google\'da yalnız satın alma kategorisindeki dönüşümler (oturum başlatma, mağaza ziyareti gibi hedefler hariç). Her platform kendi atıf modeliyle sayar; aynı satış iki platformda da görünebilir.'],
    ['İzlenme', 'Google YouTube / Demand Gen TrueView izlenmesi + Meta ThruPlay (15 sn ya da videonun tamamı).'],
    ['Erişim', 'Erişim günler ve reklamlar arasında toplanamaz. Söylem bazında Meta tekil erişimi sayfada söylem satırı açılınca Meta\'dan sorulur; Google reklam düzeyinde tekil erişim vermez.'],
  ];
  const wb = XLSX.utils.book_new();
  const s1 = XLSX.utils.json_to_sheet(ozet);
  const pctCols = ['F', 'K'];
  Object.keys(s1).forEach((k) => { if (pctCols.includes(k.replace(/\d+/g, '')) && /\d/.test(k) && k.replace(/\D/g, '') !== '1') s1[k].z = '0.0%'; });
  s1['!cols'] = [{ wch: 32 }, { wch: 16 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 8 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 8 }, { wch: 9 }, { wch: 11 }, { wch: 14 }, { wch: 8 }, { wch: 12 }, { wch: 10 }];
  XLSX.utils.book_append_sheet(wb, s1, 'Söylemler');
  const s2 = XLSX.utils.json_to_sheet(aylik); s2['!cols'] = [{ wch: 40 }, ...rapor.months.map(() => ({ wch: 11 })), { wch: 13 }];
  XLSX.utils.book_append_sheet(wb, s2, 'Aylık');
  const s3 = XLSX.utils.aoa_to_sheet(notlar); s3['!cols'] = [{ wch: 16 }, { wch: 120 }];
  XLSX.utils.book_append_sheet(wb, s3, 'Yöntem');
  const ad = `kampanya-soylemleri_${(markaAd || 'marka').toLowerCase().replace(/[^a-z0-9ığüşöç]+/gi, '-')}${butce ? `_${butce}` : ''}_${start}_${end}.xlsx`;
  XLSX.writeFile(wb, ad);
}

// ---- sayfa ----
const KOLONLAR = [
  { k: 'name', b: 'Söylem', sol: true },
  { k: 'peak_start', b: 'Yoğun dönem', sol: true },
  { k: 'cost', b: 'Harcama' }, { k: 'share', b: 'Pay' },
  { k: 'meta_cost', b: 'Meta' }, { k: 'google_cost', b: 'Google' },
  { k: 'impressions', b: 'Gösterim' }, { k: 'clicks', b: 'Tıklama' }, { k: 'ctr', b: 'TO' }, { k: 'cpc', b: 'TBM' },
  { k: 'purchases', b: 'Satış' }, { k: 'revenue', b: 'Ciro' }, { k: 'roas', b: 'ROAS' }, { k: 'video_views', b: 'İzlenme' },
  { k: 'ad_count', b: 'Reklam' },
];

export default function CampaignThemes() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || '';
  const markaAd = selectedBrand?.name || '';
  const [preset, setPreset] = useState('12');
  const [[start, end], setAralik] = useState(presetRange('12'));
  const [types, setTypes] = useState([]); // boş = tüm kampanya türleri
  const [params, setParams] = useSearchParams();
  const butce = BUTCELER.some((x) => x.key && x.key === params.get('butce')) ? params.get('butce') : '';
  const secButce = (k) => {
    const n = new URLSearchParams(params);
    if (k) n.set('butce', k); else n.delete('butce');
    setParams(n, { replace: true });
  };
  const [turlerHepsi, setTurlerHepsi] = useState([]);
  const [rapor, setRapor] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState(null);
  const [acik, setAcik] = useState(null);
  const [siralama, setSiralama] = useState({ k: 'cost', yon: -1 });
  const [gorunum, setGorunum] = useState('tablo');
  const [panel, setPanel] = useState(false);
  const [tik, setTik] = useState(0);
  const kartRef = useRef(null);
  const [kartGenislik, setKartGenislik] = useState(null);
  useEffect(() => {
    const el = kartRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setKartGenislik(el.clientWidth - 2));
    ro.observe(el);
    return () => ro.disconnect();
  }, [rapor]);

  const secPreset = (k) => { setPreset(k); const r = presetRange(k); if (r) setAralik(r); };
  const toggleTur = (t) => setTypes((l) => (l.includes(t) ? l.filter((x) => x !== t) : [...l, t]));

  useEffect(() => {
    if (!brandId) return undefined;
    let iptal = false;
    setYukleniyor(true); setHata(null);
    cagir(`/report?${qs({ start, end, types: types.length ? types.join(',') : null, budget: butce || null })}`, { brandId })
      .then((j) => { if (!iptal) { setRapor(j); setAcik(null); if (j.types_available?.length) setTurlerHepsi(j.types_available.map((x) => x.type)); } })
      .catch((e) => { if (!iptal) { setHata(e.message); setRapor(null); } })
      .finally(() => { if (!iptal) setYukleniyor(false); });
    return () => { iptal = true; };
  }, [brandId, start, end, types, butce, tik]);

  // düzenlemeden sonra yeniden hesap bitene kadar 15 sn'de bir yenile
  useEffect(() => {
    if (!rapor?.recomputing) return undefined;
    const t = setTimeout(() => setTik((x) => x + 1), 15000);
    return () => clearTimeout(t);
  }, [rapor]);

  const satirlar = useMemo(() => {
    if (!rapor) return [];
    const { k, yon } = siralama;
    return [...rapor.themes].sort((a, b) => {
      const x = k === 'name' ? etiket(a) : a[k]; const y = k === 'name' ? etiket(b) : b[k];
      if (x == null && y == null) return 0; if (x == null) return 1; if (y == null) return -1;
      return (typeof x === 'string' ? x.localeCompare(y, 'tr') : x - y) * yon;
    });
  }, [rapor, siralama]);
  const sirala = (k) => setSiralama((s) => (s.k === k ? { k, yon: -s.yon } : { k, yon: k === 'name' || k === 'peak_start' ? 1 : -1 }));

  const grafik = useMemo(() => {
    if (!rapor) return { veri: [], seriler: [] };
    const { weeks, series } = rapor.weekly;
    const veri = weeks.map((w, i) => { const o = { hafta: w }; series.forEach((s) => { o[s.key] = s.values[i]; }); return o; });
    let ci = 0;
    const seriler = series.map((s) => ({ ...s, renk: s.key === 'ao' ? AO_RENK : s.key === 'other' ? DIGER : SERI[ci++ % SERI.length] }));
    return { veri, seriler };
  }, [rapor]);

  const t = rapor?.totals;
  const bos = rapor && !((t?.cost || 0) > 0);
  const aktifSoylem = rapor ? new Set(rapor.themes.map((x) => x.theme_id)).size : 0;
  const enBuyuk = rapor?.themes?.[0];
  // izlenme başı maliyet yalnız video türleri seçiliyken anlamlı (arama/PMax harcaması izlenme üretmez)
  const videoSecili = types.length > 0 && types.every((x) => ['VIDEO', 'DEMAND_GEN', 'DISPLAY'].includes(x));

  const hucre = (s, kol) => {
    switch (kol.k) {
      case 'name': return null;
      case 'peak_start': return s.peak_start ? `${gunF(s.peak_start)} – ${gunF(s.peak_end)}` : '-';
      case 'cost': return tl(s.cost);
      case 'share': return pctF(s.share);
      case 'meta_cost': return s.meta_cost ? tl(s.meta_cost) : '-';
      case 'google_cost': return s.google_cost ? tl(s.google_cost) : '-';
      case 'impressions': return n0(s.impressions);
      case 'clicks': return n0(s.clicks);
      case 'ctr': return pctF(s.ctr, 2);
      case 'cpc': return s.cpc != null ? `₺${Number(s.cpc).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-';
      case 'purchases': return s.purchases ? n0(s.purchases) : '-';
      case 'revenue': return s.revenue ? tl(s.revenue) : '-';
      case 'roas': return roasF(s.roas);
      case 'video_views': return s.video_views ? n0(s.video_views) : '-';
      case 'ad_count': return n0(s.ad_count);
      default: return '';
    }
  };
  const maxPay = Math.max(0.0001, ...satirlar.map((s) => s.share));

  const satir = (s, ao) => {
    const ac = acik === s.key;
    return (
      <React.Fragment key={s.key}>
        <tr onClick={() => setAcik(ac ? null : s.key)} style={{ cursor: 'pointer', background: ac ? '#F5F7FF' : ao ? '#FAFAFA' : undefined }}>
          <td style={{ ...sd, textAlign: 'left', position: 'sticky', left: 0, background: ac ? '#F5F7FF' : ao ? '#FAFAFA' : T.kart, minWidth: 220, maxWidth: 320, whiteSpace: 'normal' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
              <span style={{ color: T.metin3, marginTop: 1 }}>{ac ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</span>
              <span>
                <span style={{ color: ao ? T.metin3 : T.metin, fontWeight: 500 }}>{s.name}</span>
                {s.period && <span style={{ marginLeft: 6, fontSize: 11, padding: '1px 6px', borderRadius: 6, background: T.maviAcik, color: T.mavi, whiteSpace: 'nowrap' }}>{s.period}</span>}
                {s.weak && <span title="Sürekli kampanya hattı: yalnız başka söylem olmayan reklamlarda sayılır" style={{ marginLeft: 6, fontSize: 11, color: T.turuncu, whiteSpace: 'nowrap' }}>sürekli hat</span>}
              </span>
            </div>
          </td>
          {KOLONLAR.slice(1).map((kol) => (
            <td key={kol.k} style={{ ...sd, textAlign: kol.sol ? 'left' : 'right' }}>
              {kol.k === 'share' && !ao ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ display: 'inline-block', width: 44, height: 6, background: T.cizgi2, borderRadius: 3, overflow: 'hidden' }}>
                    <span style={{ display: 'block', width: `${(s.share / maxPay) * 100}%`, height: '100%', background: T.mavi }} />
                  </span>{hucre(s, kol)}
                </span>
              ) : hucre(s, kol)}
            </td>
          ))}
        </tr>
        {ac && <tr><td colSpan={KOLONLAR.length} style={{ padding: 0 }}><KanalKirilimi satir={s} brandId={brandId} start={start} end={end} types={types} butce={butce} genislik={kartGenislik} /></td></tr>}
      </React.Fragment>
    );
  };

  return (
    <div style={{ background: T.zemin, minHeight: '100%', padding: 'clamp(12px, 3vw, 24px) clamp(12px, 3vw, 24px) 96px', color: T.metin2, fontFamily: 'inherit' }}>
      <style>{'.ct-kpi{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px}@media(max-width:900px){.ct-kpi{grid-template-columns:repeat(2,minmax(0,1fr))}}.ct-eylem{margin-left:auto;display:flex;gap:8px;flex-wrap:wrap}@media(max-width:640px){.ct-eylem{margin-left:0}}'}</style>
      <div style={{ maxWidth: 1440, margin: '0 auto' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ fontSize: 22, fontWeight: 600, color: T.metin, margin: 0 }}>Kampanya Söylemleri</h1>
          <p style={{ fontSize: 13, color: T.metin3, margin: '2px 0 0' }}>
            {markaAd ? `${markaAd} · ` : ''}Reklamlarda öne çıkan her söyleme ne kadar yatırım yapıldığı
            {butce ? ` · yalnız ${butceAd(butce).toLowerCase()} bütçesi` : ''}
            {rapor?.data?.data_end ? ` · veriler ${gunF(rapor.data.data_end)} gününe kadar, her gece güncellenir` : ''}
          </p>
        </div>

        <Kart style={{ padding: 12, marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {PRESETS.map((p) => <Cip key={p.key} aktif={preset === p.key} onClick={() => secPreset(p.key)}>{p.label}</Cip>)}
          </div>
          {preset === 'ozel' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <input type="date" value={start} min={ILK_GUN} max={end} onChange={(e) => e.target.value && setAralik([e.target.value, end])} style={{ padding: '5px 8px', fontSize: 13, border: `1px solid ${T.cizgi}`, borderRadius: 8 }} />
              <span style={{ color: T.metin3 }}>–</span>
              <input type="date" value={end} min={start} onChange={(e) => e.target.value && setAralik([start, e.target.value])} style={{ padding: '5px 8px', fontSize: 13, border: `1px solid ${T.cizgi}`, borderRadius: 8 }} />
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: T.metin3 }}>Bütçe:</span>
            {BUTCELER.map((b) => <Cip key={b.key || 'tumu'} aktif={butce === b.key} onClick={() => secButce(b.key)}>{b.label}</Cip>)}
          </div>
          {turlerHepsi.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: T.metin3 }}>Kampanya türü:</span>
              <Cip aktif={!types.length} onClick={() => setTypes([])}>Hepsi</Cip>
              {turlerHepsi.map((t) => <Cip key={t} aktif={types.includes(t)} onClick={() => toggleTur(t)}>{turAd(t)}</Cip>)}
            </div>
          )}
          <div className="ct-eylem">
            {rapor?.can_edit && <Dugme ikon={Settings2} onClick={() => setPanel(true)}>Söylem sözlüğü</Dugme>}
            <Dugme ikon={Download} onClick={() => excelYaz({ rapor, markaAd, start, end, types, butce })} disabled={!rapor || bos}>Excel</Dugme>
          </div>
        </Kart>

        {!brandId && <Kart style={{ padding: 24, textAlign: 'center', color: T.metin3 }}>Önce bir marka seçin.</Kart>}
        {hata && <Kart style={{ padding: 16, color: T.kirmizi, display: 'flex', gap: 8, alignItems: 'center' }}><AlertTriangle size={16} />Rapor yüklenemedi: {hata}</Kart>}
        {rapor?.recomputing && (
          <Kart style={{ padding: 10, marginBottom: 12, fontSize: 13, color: T.mavi, display: 'flex', gap: 8, alignItems: 'center', background: T.maviAcik, borderColor: '#C7D2FE' }}>
            <RefreshCw size={15} /> Söylem sözlüğü değişti, rakamlar yeniden hesaplanıyor. Sayfa kendiliğinden yenilenecek.
          </Kart>
        )}
        {yukleniyor && !rapor && <Kart style={{ padding: 24, textAlign: 'center', color: T.metin3 }}>Yükleniyor…</Kart>}
        {bos && <Kart style={{ padding: 24, textAlign: 'center', color: T.metin3 }}>Bu aralıkta reklam harcaması yok.</Kart>}

        {rapor && !bos && (
          <div style={{ opacity: yukleniyor ? 0.6 : 1, transition: 'opacity .15s' }}>
            <div className="ct-kpi" style={{ marginBottom: 16 }}>
              <Kpi baslik="Toplam reklam harcaması" deger={tl(t.cost)} alt={[t.google_cost > 0 && `Google ${tlKisa(t.google_cost)}`, t.meta_cost > 0 && `Meta ${tlKisa(t.meta_cost)}`].filter(Boolean).join(' · ') || ' '} />
              <Kpi baslik="Söylem taşıyan harcama" deger={tl(rapor.themed.cost)} alt={`Toplamın ${pctF(rapor.themed.share)}'i · ${aktifSoylem} söylem`} />
              <Kpi baslik="Always-on / söylemsiz" deger={tl(rapor.always_on.cost)} alt={`Toplamın ${pctF(rapor.always_on.share)}'i`} />
              <Kpi baslik="Satış · ciro" deger={tl(t.revenue)} alt={`${n0(t.purchases)} satış · ROAS ${roasF(t.roas)}`} />
              <Kpi baslik="Video izlenme" deger={n0(t.video_views)} alt={videoSecili && t.cpv != null ? `İzlenme başı ₺${Number(t.cpv).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'YouTube, Demand Gen ve Meta video'} />
              <Kpi baslik="En çok yatırım alan söylem" deger={enBuyuk ? etiket(enBuyuk) : '-'} alt={enBuyuk ? `${tl(enBuyuk.cost)} · ${pctF(enBuyuk.share)}` : ''} />
            </div>

            <Kart style={{ padding: '14px 12px 6px', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', padding: '0 4px 8px' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.metin }}>Haftalık harcama, söyleme göre</div>
              </div>
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={grafik.veri} margin={{ top: 4, right: 8, left: 0, bottom: 0 }} barCategoryGap={1}>
                    <CartesianGrid vertical={false} stroke={T.cizgi2} />
                    <XAxis dataKey="hafta" tickFormatter={gunF} tick={{ fontSize: 11, fill: T.metin3 }} tickLine={false} axisLine={{ stroke: T.cizgi }} minTickGap={28} />
                    <YAxis tickFormatter={tlKisa} tick={{ fontSize: 11, fill: T.metin3 }} tickLine={false} axisLine={false} width={64} />
                    <Tooltip
                      cursor={{ fill: 'rgba(67,106,235,0.06)' }}
                      content={({ active, payload, label }) => {
                        if (!active || !payload || !payload.length) return null;
                        const ps = payload.filter((p) => p.value > 0).sort((a, b) => b.value - a.value);
                        const top = ps.reduce((a, p) => a + p.value, 0);
                        return (
                          <div style={{ background: T.kart, border: `1px solid ${T.cizgi}`, borderRadius: 8, padding: '8px 10px', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.06)', maxWidth: 300 }}>
                            <div style={{ fontWeight: 600, color: T.metin, marginBottom: 4 }}>{gunF(label)} haftası · {tl(top)}</div>
                            {ps.map((p) => (
                              <div key={p.dataKey} style={{ display: 'flex', gap: 6, alignItems: 'center', ...TABULAR }}>
                                <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color, flex: 'none' }} />
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                                <span>{tlKisa(p.value)}</span>
                              </div>
                            ))}
                          </div>
                        );
                      }}
                    />
                    {grafik.seriler.map((s) => <Bar key={s.key} dataKey={s.key} name={s.name} stackId="a" fill={s.renk} isAnimationActive={false} />)}
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', padding: '8px 4px 8px' }}>
                {grafik.seriler.map((s) => (
                  <span key={s.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: T.metin2 }}>
                    <span style={{ width: 9, height: 9, borderRadius: 2, background: s.renk }} />{s.name}
                  </span>
                ))}
              </div>
            </Kart>

            <div ref={kartRef}><Kart style={{ marginBottom: 16, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, padding: '12px 14px', borderBottom: `1px solid ${T.cizgi}` }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.metin }}>Söylemler</div>
                <span style={{ fontSize: 12, color: T.metin3 }}>Satıra tıklayınca söylemin kanal kırılımı açılır</span>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                  <Cip aktif={gorunum === 'tablo'} onClick={() => setGorunum('tablo')}>Özet</Cip>
                  <Cip aktif={gorunum === 'ay'} onClick={() => setGorunum('ay')}>Aylık</Cip>
                </div>
              </div>
              {gorunum === 'tablo' ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr>
                      {KOLONLAR.map((kol, i) => (
                        <th key={kol.k} onClick={() => sirala(kol.k)} style={{ ...sb, textAlign: kol.sol ? 'left' : 'right', ...(i === 0 ? { left: 0, zIndex: 2 } : {}) }}>
                          {kol.b}{siralama.k === kol.k ? (siralama.yon < 0 ? ' ↓' : ' ↑') : ''}
                        </th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {satirlar.map((s) => satir(s, false))}
                      {rapor.always_on.cost > 0 && satir(rapor.always_on, true)}
                      <tr style={{ background: '#F5F7FD' }}>
                        <td style={{ ...sd, textAlign: 'left', fontWeight: 600, color: T.metin, position: 'sticky', left: 0, background: '#F5F7FD' }}>Toplam</td>
                        <td style={sd} />
                        <td style={{ ...sd, fontWeight: 600, color: T.metin }}>{tl(t.cost)}</td>
                        <td style={sd}>%100</td>
                        <td style={sd}>{tl(t.meta_cost)}</td>
                        <td style={sd}>{tl(t.google_cost)}</td>
                        <td style={sd}>{n0(t.impressions)}</td>
                        <td style={sd}>{n0(t.clicks)}</td>
                        <td style={sd}>{pctF(t.ctr, 2)}</td>
                        <td style={sd}>{t.cpc != null ? `₺${Number(t.cpc).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}` : '-'}</td>
                        <td style={sd}>{n0(t.purchases)}</td>
                        <td style={sd}>{tl(t.revenue)}</td>
                        <td style={sd}>{roasF(t.roas)}</td>
                        <td style={sd}>{n0(t.video_views)}</td>
                        <td style={sd}>{n0(t.ad_count)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr>
                      <th style={{ ...sb, textAlign: 'left', left: 0, zIndex: 2, cursor: 'default' }}>Söylem</th>
                      {rapor.months.map((m) => <th key={m} style={{ ...sb, cursor: 'default' }}>{ayF(m)}</th>)}
                      <th style={{ ...sb, cursor: 'default' }}>Toplam</th>
                    </tr></thead>
                    <tbody>
                      {[...satirlar, rapor.always_on].filter((s) => s.cost > 0).map((s) => {
                        const ao = s.theme_id == null;
                        const ayMax = Math.max(1, ...rapor.months.map((m) => (s.monthly || {})[m] || 0));
                        return (
                          <tr key={s.key} style={ao ? { background: '#FAFAFA' } : undefined}>
                            <td style={{ ...sd, textAlign: 'left', position: 'sticky', left: 0, background: ao ? '#FAFAFA' : T.kart, color: ao ? T.metin3 : T.metin, minWidth: 220 }}>{etiket(s)}</td>
                            {rapor.months.map((m) => {
                              const v = (s.monthly || {})[m] || 0;
                              const a = ao ? 0 : Math.min(0.85, v / ayMax);
                              return <td key={m} style={{ ...sd, background: a > 0.02 ? `rgba(67,106,235,${a * 0.35})` : undefined, color: v ? T.metin2 : T.cizgi }}>{v >= 1 ? tlKisa(v) : '·'}</td>;
                            })}
                            <td style={{ ...sd, fontWeight: 600, color: T.metin }}>{tlKisa(s.cost)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Kart></div>

            <Kart style={{ padding: '12px 14px', fontSize: 12, color: T.metin3, lineHeight: 1.6 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontWeight: 600, color: T.metin2, marginBottom: 4 }}><Info size={14} />Nasıl hesaplanıyor</div>
              <div>Söylem yalnız reklamın kendisinden okunur: reklam ve kampanya adı, reklam metni, Google'da o gün yayında olan başlık ve açıklamalar. Söyleme yatırım, o söylemi taşıyan reklamlara o günlerde harcanan tutardır. Söylem taşımayan harcama always-on satırındadır, toplam platform harcamasıyla aynıdır.</div>
              <div>Google Performance Max reklamlarında günlük başlık verisi olmadığı için metindeki söylem, yalnız markanın başka reklamlarında aynı günlerde yayında olduğu dönemde sayılır. Aynı gün bir reklamda birden fazla söylem varsa harcama aralarında bölünür.</div>
              <div>Satış ve ciro: Meta satın almaları ile Google'da yalnız satın alma kategorisindeki dönüşümler (oturum başlatma, mağaza ziyareti gibi hedefler hariç). Her platform kendi atıf modeliyle sayar. İzlenme: Google TrueView (YouTube, Demand Gen) + Meta ThruPlay. Erişim toplanamadığı için söylem satırı açılınca Meta'dan tekil olarak sorulur.</div>
              {rapor.reconciliation?.by_platform && Object.keys(rapor.reconciliation.by_platform).length > 0 && (
                <div style={{ marginTop: 4 }}>
                  Mutabakat: {Object.entries(rapor.reconciliation.by_platform).map(([p, r]) => `${PLAT_AD[p]} ${tl(r.ads_total)} (hesap toplamına göre ${r.diff_pct >= 0 ? '+' : ''}${r.diff_pct.toLocaleString('tr-TR')}%)`).join(' · ')}
                </div>
              )}
            </Kart>
          </div>
        )}
      </div>
      {panel && <YonetimPaneli brandId={brandId} start={start} end={end} onKapat={() => setPanel(false)} onDegisti={() => setTimeout(() => setTik((x) => x + 1), 1500)} />}
    </div>
  );
}
